import { NextPageContext, NextComponentType } from 'next';
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import initApollo from '@utils/apollo/initApollo';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextRouter } from 'next/router';
import logger from '@utils/logger';
import {
  AppContextType,
  AppInitialProps,
  AppPropsType,
} from 'next/dist/shared/lib/utils';
import { Channel } from '@monofront/business/constants';
import { getChannelFromLocale } from '@monofront/business/utils';
import { NextIntlClientProvider } from 'next-intl';

interface ApolloAppContextType extends AppContextType<NextRouter> {
  ctx: NextPageContext & {
    apolloClient?: ApolloClient<NormalizedCacheObject>;
  };
}

interface IApolloInitialProps {
  apolloState: NormalizedCacheObject;
  channel: Channel;
}

type ApolloAppType = NextComponentType<
  AppContextType,
  AppInitialProps & IApolloInitialProps,
  AppPropsType & {
    apolloClient: ApolloClient<NormalizedCacheObject>;
  }
>;

const withApollo = (App: ApolloAppType) => {
  const WithApollo = (props: AppPropsType & IApolloInitialProps) => {
    const apolloClient = initApollo(props.channel, props.apolloState);

    return <App apolloClient={apolloClient} {...props} />;
  };

  WithApollo.displayName = 'withApollo(App)';

  WithApollo.getInitialProps = async (
    appContext: ApolloAppContextType,
  ): Promise<AppInitialProps & IApolloInitialProps> => {
    const { AppTree, ctx, Component, router } = appContext;

    const channel = getChannelFromLocale(ctx.locale);

    let appProps: AppInitialProps = { pageProps: {} };

    if (App.getInitialProps) {
      appProps = await App.getInitialProps(appContext);
    }

    if (ctx.res?.writableEnded) {
      return { pageProps: {}, apolloState: {}, channel };
    }

    const apollo = initApollo(channel);
    const isRunningOnServer = typeof window === 'undefined';

    if (isRunningOnServer && Component.getInitialProps) {
      try {
        await getDataFromTree(
          <ApolloProvider client={apollo}>
            <NextIntlClientProvider
              locale={ctx.locale}
              messages={appProps.pageProps.messages}
            >
              <AppTree
                {...appProps}
                apolloClient={apollo}
                Component={Component}
                router={router}
              />
            </NextIntlClientProvider>
          </ApolloProvider>,
        );
      } catch (e: any) {
        logger.error('Error while running getDatafromTree', e);
      }
    }

    const apolloState = apollo.cache.extract();

    return {
      ...appProps,
      apolloState,
      channel,
    };
  };

  return WithApollo;
};

export default withApollo;
